import { useFormContext, Controller } from "react-hook-form";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import Combobox from "@/components/ui/common/combobox";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Consultation } from "@/types/consultation";
import { useGetAllMemberQuery, useGetMemberInSystemQuery } from "@/services/memberApi";
import { useGetCoachesQuery, useGetCoachInSystemQuery } from "@/services/coachApi";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "@radix-ui/react-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormControl } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { displayDate } from "@/lib/helper";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const Background = ({ action, open }: FormStepProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<Consultation>();

  const watcher = watch();

  // Fetch members
  const { data: memberData } = useGetAllMemberQuery({
    query: "offset=0&sort_key=id&sort_order=desc",
    org_id: orgId,
  });

  // Fetch coaches
  const { data: coachData } = useGetCoachesQuery({
    query: "offset=0&sort_key=id&sort_order=desc",
    org_id: orgId,
  });

  // Create member options
  const memberOptions = useMemo(() => {
    if (!Array.isArray(memberData?.data)) {
      return [];
    }
    return memberData?.data.map((member) => {
      const firstName = (member?.first_name || "").trim();
      const lastName = (member?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");

      const labelPrefix = member.own_member_id
        ? `${member.own_member_id} • `
        : "";
      const label = `${labelPrefix}${fullName}`;

      return {
        label: label,
        value: member.id?.toString() || "",
      };
    });
  }, [memberData]);

  // Create coach options
  const coachOptions = useMemo(() => {
    if (!Array.isArray(coachData?.data)) {
      return [];
    }
    return coachData?.data.map((coach) => {
      const firstName = (coach?.first_name || "").trim();
      const lastName = (coach?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");

      const labelPrefix = coach.own_coach_id ? `${coach.own_coach_id} • ` : "";
      const label = `${labelPrefix}${fullName}`;

      return {
        label: label,
        value: coach.id?.toString() || "",
      };
    });
  }, [coachData]);

  // Get current date in YYYY-MM-DD format
  const currentDate = format(new Date(), "yyyy-MM-dd");

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <p className="font-medium text-sm">Consultation Details</p>
        <Separator className="h-[1px] mt-2" />
        <div className="grid grid-cols-3  gap-4 mt-4">
          {/* Member Selection */}
          <div>
            <Controller
              name="member_id"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Member</p>
                  <Combobox
                    list={memberOptions}
                    defaultValue={
                      memberOptions.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = memberOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      if (selectedOption) {
                        onChange(Number(selectedOption.value));
                        // Also update member_name
                        const selectedMember = memberData?.data.find(
                          (m) => m.id?.toString() === selectedOption.value
                        );
                        if (selectedMember) {
                          const firstName = (selectedMember?.first_name || "").trim();
                          const lastName = (selectedMember?.last_name || "").trim();
                          const fullName = [firstName, lastName]
                            .filter(Boolean)
                            .join(" ");
                          setValue("member_name", fullName);
                        }
                      } else {
                        onChange(undefined);
                        setValue("member_name", "");
                      }
                    }}
                    label="Member"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Coach Selection */}
          <div>
            <Controller
              name="coach_id"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Coach</p>
                  <Combobox
                    list={coachOptions}
                    defaultValue={
                      coachOptions.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = coachOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      if (selectedOption) {
                        onChange(Number(selectedOption.value));
                        // Also update coach_name
                        const selectedCoach = coachData?.data.find(
                          (c) => c.id?.toString() === selectedOption.value
                        );
                        if (selectedCoach) {
                          const firstName = (selectedCoach?.first_name || "").trim();
                          const lastName = (selectedCoach?.last_name || "").trim();
                          const fullName = [firstName, lastName]
                            .filter(Boolean)
                            .join(" ");
                          setValue("coach_name", fullName);
                        }
                      } else {
                        onChange(undefined);
                        setValue("coach_name", "");
                      }
                    }}
                    label="Coach"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Date Selection */}
          <div>
            <Controller
              name="date"
              control={control}
              render={({
                field: { value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Date</p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <div className="relative">
                          <Button
                            variant={"outline"}
                            type="button"
                            disabled
                            className={cn(
                              "w-full pl-3 text-gray-800 text-left font-normal border-gray-300 border-[1px] cursor-not-allowed opacity-100"
                            )}
                          >
                            {value ? (
                              displayDate(new Date(value))
                            ) : (
                              <span>Select date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                          {error && (
                            <span className="text-red-500 text-xs mt-[5px]">
                              {error.message}
                            </span>
                          )}
                        </div>
                      </FormControl>
                    </PopoverTrigger>
                    {/* <PopoverContent
                      className="w-auto p-0"
                      align="center"
                    >
                      <Calendar
                        mode="single"
                        captionLayout="dropdown-buttons"
                        selected={value ? new Date(value) : undefined}
                        defaultMonth={
                          value ? new Date(value) : new Date()
                        }
                        disabled
                        fromYear={1950}
                        toYear={new Date().getFullYear()}
                        initialFocus
                      />
                    </PopoverContent> */}
                  </Popover>
                </>
              )}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Background;

